import React, { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { PasswordInput } from "./ui/password-input";
import { Label } from "./ui/label";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function ChangePasswordDialog({ open, onOpenChange }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const reset = () => { setCurrent(""); setNext(""); setConfirm(""); setError(""); };

  const handleOpenChange = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next.length < 6) { setError("New password must be at least 6 characters."); return; }
    if (next !== confirm) { setError("New passwords do not match."); return; }
    if (next === current) { setError("New password must be different from your current password."); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      reset();
      onOpenChange(false);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Could not change password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle>Change password</DialogTitle>
          <DialogDescription>Enter your current password and choose a new one.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="change-password-form">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Current password</Label>
            <PasswordInput required value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="change-current-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">New password</Label>
            <PasswordInput required minLength={6} value={next} onChange={(e) => setNext(e.target.value)} data-testid="change-new-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Confirm new password</Label>
            <PasswordInput required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="change-confirm-input" />
          </div>
          {error && <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{error}</div>}
          <DialogFooter>
            <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="change-password-submit">
              {saving ? "Updating…" : "Update password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
